import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { navigate, useLocation, useToast, useTRPC } from '@barev/web';
import { useEffect, useMemo, useState } from 'react';
import { errText, useT, type BoLocale } from '../i18n.tsx';
import { Dialog, download, Field, I, Skeleton } from '../components/kit.tsx';

type Tab = 'accrue' | 'requests' | 'suggestions' | 'ledger' | 'report' | 'import' | 'setup';
const TABS: Tab[] = ['accrue', 'requests', 'suggestions', 'ledger', 'report', 'import', 'setup'];
const TIER = { BAREV: 'Barev', BAREV_PLUS: 'Barev+', ELITE: 'Elite' } as Record<string, string>;
const pick = (v: Record<string, string> | null | undefined, l: BoLocale) => (v ? v[l] || v.ru || v.en || '' : '');
const key = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);
const fmt = (n: number) => new Intl.NumberFormat('ru-RU').format(n);
const todayKey = () => new Date(Date.now() + 4 * 3600_000).toISOString().slice(0, 10);

/** Барики: начисление, запросы клиентов, предложения системы, журнал, отчёт, импорт, справочники */
export function Points() {
  const { t } = useT();
  const trpc = useTRPC();
  const { query } = useLocation();
  const tab = (TABS.includes(query.get('tab') as Tab) ? query.get('tab') : 'accrue') as Tab;
  const reqs = useQuery({ ...trpc.bo.points.requests.queryOptions(), refetchInterval: 30_000 });
  const sugg = useQuery({ ...trpc.bo.points.suggestions.queryOptions(), refetchInterval: 60_000 });
  const dict = useQuery(trpc.bo.points.dictionaries.queryOptions());
  const go = (t2: Tab) => navigate(`/points?tab=${t2}`);
  const count: Partial<Record<Tab, number>> = { requests: reqs.data?.length, suggestions: sugg.data?.length };
  const tabs = TABS.filter((x) => dict.data?.owner || !['report', 'setup', 'suggestions'].includes(x));
  return (
    <>
      <div className="bo-head">
        <h1 className="bo-title">{t('pt.title')}</h1>
      </div>
      <div className="bo-chtabs bo-pt-tabs">
        {tabs.map((x) => (
          <button key={x} type="button" aria-pressed={tab === x} onClick={() => go(x)}>
            {t(`pt.tab.${x}`)}
            {!!count[x] && <span className="bo-nav__count">{count[x]}</span>}
          </button>
        ))}
      </div>
      {!dict.data ? (
        <Skeleton h={260} />
      ) : tab === 'accrue' ? (
        <Accrue dict={dict.data} initialClient={query.get('client')} />
      ) : tab === 'requests' ? (
        <Requests />
      ) : tab === 'suggestions' ? (
        <Suggestions />
      ) : tab === 'ledger' ? (
        <Ledger dict={dict.data} initialClient={query.get('client')} />
      ) : tab === 'report' ? (
        <Report />
      ) : tab === 'import' ? (
        <Import />
      ) : (
        <Setup dict={dict.data} />
      )}
    </>
  );
}

type Dict = NonNullable<ReturnType<typeof useDict>['data']>;
function useDict() {
  const trpc = useTRPC();
  return useQuery(trpc.bo.points.dictionaries.queryOptions());
}

// ───── начисление ─────

function Accrue({ dict, initialClient }: { dict: Dict; initialClient: string | null }) {
  const { t } = useT();
  const trpc = useTRPC();
  const [q, setQ] = useState('');
  const [clientId, setClientId] = useState<string | null>(initialClient);
  const found = useQuery({ ...trpc.bo.points.search.queryOptions({ q }), enabled: q.trim().length >= 2 });
  return (
    <div className="bo-client-grid">
      <div className="bo-col">
        <section className="bo-panel">
          <Field label={t('pt.search')}>
            <input className="bo-input" value={q} placeholder={t('pt.searchPh')} onChange={(e) => setQ(e.target.value)} autoFocus />
          </Field>
          {found.data && (
            <ul className="bo-list">
              {found.data.length === 0 && <li className="bo-hint">{t('pt.notFound')}</li>}
              {found.data.map((c) => (
                <li key={c.id} className="bo-pt-pick" data-on={c.id === clientId || undefined} onClick={() => setClientId(c.id)}>
                  <span className="bo-grow">
                    <b>{c.name}</b>
                    <span className="bo-small bo-faint" style={{ display: 'block' }}>
                      {[c.phone, c.username && `@${c.username}`, TIER[c.tier]].filter(Boolean).join(' · ')}
                    </span>
                  </span>
                  <b className="bo-num">{fmt(c.balance)}</b>
                </li>
              ))}
            </ul>
          )}
        </section>
        {clientId && <ClientPoints id={clientId} />}
      </div>
      <div className="bo-col">{clientId ? <AccrueForm dict={dict} clientId={clientId} /> : <div className="bo-panel bo-empty">{t('pt.pickClient')}</div>}</div>
    </div>
  );
}

/** Карточка клиента: баланс, ожидание, сгорание, последние операции с отменой */
export function ClientPoints({ id, compact }: { id: string; compact?: boolean }) {
  const { t, date, locale } = useT();
  const trpc = useTRPC();
  const c = useQuery(trpc.bo.points.client.queryOptions({ id }));
  const [rev, setRev] = useState<{ type: 'ACCRUAL' | 'REDEMPTION'; id: string } | null>(null);
  const [redeem, setRedeem] = useState(false);
  if (!c.data) return <Skeleton h={180} />;
  const d = c.data;
  return (
    <section className="bo-panel">
      <div className="bo-panel__head">
        <h2 className="bo-h2">{compact ? t('pt.title') : d.name}</h2>
        {d.flagged && <span className="bo-chip bo-chip--danger">{t('pt.flagged')}</span>}
      </div>
      <div className="bo-stats" style={{ marginBottom: 12 }}>
        <span>
          <span className="bo-small bo-faint">{t('pt.balance')}</span>
          <b style={{ color: d.balance < 0 ? 'var(--bc-danger)' : undefined }}>{fmt(d.balance)}</b>
        </span>
        <span>
          <span className="bo-small bo-faint">{t('pt.pending')}</span>
          <b>{fmt(d.pending)}</b>
          {d.pendingNextAt && <span className="bo-small bo-faint">{t('pt.availableFrom', { date: date(d.pendingNextAt) })}</span>}
        </span>
      </div>
      <dl className="bo-kv" style={{ marginBottom: 12 }}>
        <dt>{t('pt.tier')}</dt>
        <dd>{TIER[d.tier]}</dd>
        <dt>{t('pt.burnAt')}</dt>
        <dd>{d.burnAt ? date(d.burnAt) : '—'}</dd>
        <dt>{t('pt.agency')}</dt>
        <dd>{d.agency ?? '—'}</dd>
        <dt>{t('pt.streak')}</dt>
        <dd>{d.streak}</dd>
        {d.debt > 0 && (
          <>
            <dt>{t('pt.debt')}</dt>
            <dd style={{ color: 'var(--bc-danger)' }}>{fmt(d.debt)}</dd>
          </>
        )}
      </dl>
      <div className="bo-row" style={{ gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <button type="button" className="bo-btn bo-btn--sm" disabled={d.balance <= 0} onClick={() => setRedeem(true)}>
          {t('pt.redeem')}
        </button>
        {compact && (
          <a className="bo-btn bo-btn--sm bo-btn--ghost" href={`#/points?tab=accrue&client=${d.id}`}>
            {t('pt.accrueBtn')}
          </a>
        )}
        <a className="bo-btn bo-btn--sm bo-btn--ghost" href={`#/points?tab=ledger&client=${d.id}`}>
          {t('pt.allOps')}
        </a>
      </div>
      {d.history.length === 0 ? (
        <p className="bo-hint">{t('pt.noOps')}</p>
      ) : (
        <ul className="bo-list">
          {d.history.map((h) => (
            <li key={`${h.type}:${h.id}`}>
              <span className="bo-grow">
                <b>{h.type === 'bonus' ? pick(h.bonus, locale) : h.type === 'accrual' ? pick(h.category, locale) : t(`pt.type.${h.type}`)}</b>
                {h.orderRef && <span className="bo-faint"> {h.orderRef}</span>}
                <span className="bo-small bo-faint" style={{ display: 'block' }}>
                  {date(h.at)} · {t(`pt.status.${h.status}`)}
                </span>
              </span>
              <b className="bo-num" style={{ color: h.amount < 0 ? 'var(--bc-danger)' : undefined }}>
                {h.amount > 0 ? '+' : '−'}
                {fmt(Math.abs(h.amount))}
              </b>
              {((h.type === 'accrual' || h.type === 'bonus') && (h.status === 'PENDING' || h.status === 'ACTIVE')) || (h.type === 'redemption' && h.status === 'CONFIRMED') ? (
                <button type="button" className="bo-btn bo-btn--sm bo-btn--ghost" onClick={() => setRev({ type: h.type === 'redemption' ? 'REDEMPTION' : 'ACCRUAL', id: h.id })}>
                  {t('pt.reverse')}
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {rev && <ReverseDialog target={rev} onClose={() => setRev(null)} />}
      {redeem && <RedeemDialog clientId={d.id} balance={d.balance} onClose={() => setRedeem(false)} />}
    </section>
  );
}

function AccrueForm({ dict, clientId }: { dict: Dict; clientId: string }) {
  const { t, locale, date } = useT();
  const trpc = useTRPC();
  const qc = useQueryClient();
  const toast = useToast();
  const [kind, setKind] = useState<'ORDER' | 'BONUS'>('ORDER');
  const cats = dict.categories.filter((c) => c.active);
  const bonuses = dict.bonusTypes.filter((b) => b.active);
  const [f, setF] = useState({ categoryId: cats[0]?.id ?? '', bonusTypeId: bonuses[0]?.id ?? '', orderAmount: '', excluded: '', paidWithPoints: '', serviceDate: todayKey(), orderRef: '', amount: '', reason: '', comment: '', noWait: false, ttl: '' });
  const [confirm, setConfirm] = useState(false);
  const [idem, setIdem] = useState(key);
  useEffect(() => setIdem(key()), [clientId]);
  const serviceDate = new Date(`${f.serviceDate}T12:00:00+04:00`);
  const preview = useQuery({
    ...trpc.bo.points.preview.queryOptions({ clientId, categoryId: f.categoryId, orderAmount: Number(f.orderAmount) || 0, excludedAmount: Number(f.excluded) || 0, paidWithPoints: Number(f.paidWithPoints) || 0, serviceDate }),
    enabled: kind === 'ORDER' && !!f.categoryId && !Number.isNaN(serviceDate.getTime()),
  });
  const client = useQuery(trpc.bo.points.client.queryOptions({ id: clientId }));
  const bonus = bonuses.find((b) => b.id === f.bonusTypeId);
  const suggested = kind === 'ORDER' ? (preview.data?.suggested ?? 0) : (bonus?.defaultAmount ?? 0);
  const amount = f.amount === '' ? suggested : Number(f.amount);
  const changed = f.amount !== '' && amount !== suggested;
  const needReason = changed || (f.noWait && !dict.owner);
  const ttlDefault = bonus?.ttlDays ?? null;
  const ttl = f.ttl === '' ? ttlDefault : Number(f.ttl);
  const save = useMutation(
    trpc.bo.points.accrue.mutationOptions({
      onSuccess: (r) => {
        toast(r.status === 'needs_approval' ? t('pt.needsApproval') : r.status === 'skipped' ? t('pt.zero') : t('pt.accrued', { n: fmt(r.status === 'created' ? r.amount : 0) }));
        void qc.invalidateQueries();
        setConfirm(false);
        setIdem(key());
        setF({ ...f, orderAmount: '', excluded: '', paidWithPoints: '', orderRef: '', amount: '', reason: '', comment: '', noWait: false, ttl: '' });
      },
      onError: (e) => toast(errText(t, e), 'error'),
    }),
  );
  const submit = () =>
    save.mutate({
      clientId,
      kind,
      categoryId: kind === 'ORDER' ? f.categoryId : undefined,
      bonusTypeId: kind === 'BONUS' ? f.bonusTypeId : undefined,
      orderAmount: kind === 'ORDER' ? Number(f.orderAmount) || 0 : undefined,
      excludedAmount: Number(f.excluded) || undefined,
      paidWithPoints: Number(f.paidWithPoints) || undefined,
      serviceDate,
      orderRef: f.orderRef.trim() || null,
      amount: f.amount === '' ? undefined : amount,
      reason: f.reason.trim() || null,
      commentForClient: f.comment.trim() || null,
      noWait: f.noWait,
      ttlDays: kind === 'BONUS' && f.ttl !== '' ? ttl : undefined,
      idempotencyKey: idem,
    });
  const num = (v: string) => v.replace(/\D/g, '').slice(0, 9);
  const cat = cats.find((c) => c.id === f.categoryId);
  const valid = amount > 0 && (!needReason || f.reason.trim()) && (kind === 'BONUS' || Number(f.orderAmount) > 0);
  return (
    <section className="bo-panel">
      <div className="bo-panel__head">
        <h2 className="bo-h2">{t('pt.accrueTitle')}</h2>
      </div>
      <div className="bo-chtabs" style={{ marginBottom: 14 }}>
        {(['ORDER', 'BONUS'] as const).map((k) => (
          <button key={k} type="button" aria-pressed={kind === k} onClick={() => setKind(k)}>
            {t(`pt.kind.${k}`)}
          </button>
        ))}
      </div>
      {kind === 'ORDER' ? (
        <>
          <div className="bo-row2">
            <Field label={t('pt.f.category')}>
              <select className="bo-select" value={f.categoryId} onChange={(e) => setF({ ...f, categoryId: e.target.value })}>
                {cats.map((c) => (
                  <option key={c.id} value={c.id}>
                    {pick(c.name, locale)}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={t('pt.f.orderAmount')}>
              <input className="bo-input" inputMode="numeric" value={f.orderAmount} onChange={(e) => setF({ ...f, orderAmount: num(e.target.value) })} />
            </Field>
          </div>
          <div className="bo-row2">
            <Field label={t('pt.f.excluded')} hint={t('pt.f.excludedHint')}>
              <input className="bo-input" inputMode="numeric" value={f.excluded} onChange={(e) => setF({ ...f, excluded: num(e.target.value) })} />
            </Field>
            <Field label={t('pt.f.paidWithPoints')}>
              <input className="bo-input" inputMode="numeric" value={f.paidWithPoints} onChange={(e) => setF({ ...f, paidWithPoints: num(e.target.value) })} />
            </Field>
          </div>
          <div className="bo-row2">
            <Field label={t('pt.f.serviceDate')}>
              <input className="bo-input" type="date" value={f.serviceDate} onChange={(e) => setF({ ...f, serviceDate: e.target.value })} />
            </Field>
            <Field label={t('pt.f.orderRef')} hint={t('pt.f.orderRefHint')}>
              <input className="bo-input" value={f.orderRef} maxLength={60} onChange={(e) => setF({ ...f, orderRef: e.target.value })} />
            </Field>
          </div>
          {preview.data && (
            <p className="bo-side-panel__note" style={{ margin: '0 0 12px' }}>
              {t('pt.formula', { tier: TIER[preview.data.tier], rate: preview.data.baseRatePct, extra: preview.data.streakBonusPct ? t('pt.formulaStreak', { streak: preview.data.streak, bonus: preview.data.streakBonusPct }) : '', n: fmt(preview.data.suggested) })}
            </p>
          )}
        </>
      ) : (
        <div className="bo-row2">
          <Field label={t('pt.f.bonusType')}>
            <select className="bo-select" value={f.bonusTypeId} onChange={(e) => setF({ ...f, bonusTypeId: e.target.value, amount: '', ttl: '' })}>
              {bonuses.map((b) => (
                <option key={b.id} value={b.id}>
                  {pick(b.name, locale)}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('pt.f.ttl')} hint={ttlDefault ? t('pt.f.ttlDefault', { n: ttlDefault }) : t('pt.f.ttlNone')}>
            <input className="bo-input" inputMode="numeric" placeholder={ttlDefault ? String(ttlDefault) : '—'} value={f.ttl} onChange={(e) => setF({ ...f, ttl: num(e.target.value).slice(0, 4) })} />
          </Field>
        </div>
      )}
      <div className="bo-row2">
        <Field label={t('pt.f.amount')} hint={t('pt.f.amountHint', { n: fmt(suggested) })}>
          <input className="bo-input" inputMode="numeric" placeholder={String(suggested)} value={f.amount} onChange={(e) => setF({ ...f, amount: num(e.target.value) })} />
        </Field>
        <Field label={t(needReason ? 'pt.f.reasonRequired' : 'pt.f.reason')}>
          <input className="bo-input" value={f.reason} maxLength={500} onChange={(e) => setF({ ...f, reason: e.target.value })} />
        </Field>
      </div>
      <Field label={t('pt.f.comment')}>
        <input className="bo-input" value={f.comment} maxLength={300} placeholder={t('pt.f.commentPh')} onChange={(e) => setF({ ...f, comment: e.target.value })} />
      </Field>
      {kind === 'ORDER' && (
        <label className="bo-check" style={{ marginBottom: 12 }}>
          <input type="checkbox" checked={f.noWait} onChange={(e) => setF({ ...f, noWait: e.target.checked })} />
          {t(dict.owner ? 'pt.f.noWait' : 'pt.f.noWaitReason')}
        </label>
      )}
      <button type="button" className="bo-btn bo-btn--accent" disabled={!valid || save.isPending} onClick={() => setConfirm(true)}>
        {t('pt.accrueBtn')}
      </button>
      {confirm && (
        <Dialog
          open
          onClose={() => setConfirm(false)}
          title={t('pt.confirmTitle')}
          footer={
            <>
              <button type="button" className="bo-btn bo-btn--ghost" onClick={() => setConfirm(false)}>
                {t('pt.edit')}
              </button>
              <button type="button" className="bo-btn" disabled={save.isPending} onClick={submit}>
                {t('pt.accrueBtn')}
              </button>
            </>
          }
        >
          <p style={{ margin: 0, fontSize: 17, lineHeight: 1.45 }}>
            {kind === 'ORDER'
              ? t('pt.confirmOrder', {
                  n: fmt(amount),
                  name: client.data?.name ?? '',
                  what: pick(cat?.name, locale).toLowerCase(),
                  sum: fmt(Number(f.orderAmount) || 0),
                  tier: TIER[preview.data?.tier ?? 'BAREV'],
                  rate: preview.data?.ratePct ?? 0,
                  date: f.noWait || !preview.data ? t('pt.now') : date(preview.data.availableAt),
                })
              : t('pt.confirmBonus', { n: fmt(amount), name: client.data?.name ?? '', what: pick(bonus?.name, locale), until: ttl ? t('pt.forDays', { n: ttl }) : t('pt.noTtl') })}
          </p>
          {changed && <p className="bo-hint">{t('pt.changedNote', { n: fmt(suggested) })}</p>}
        </Dialog>
      )}
    </section>
  );
}

function ReverseDialog({ target, onClose }: { target: { type: 'ACCRUAL' | 'REDEMPTION'; id: string }; onClose: () => void }) {
  const { t } = useT();
  const trpc = useTRPC();
  const qc = useQueryClient();
  const toast = useToast();
  const p = useQuery(trpc.bo.points.reversePreview.queryOptions(target));
  const [reason, setReason] = useState('');
  const save = useMutation(
    trpc.bo.points.reverse.mutationOptions({
      onSuccess: () => {
        toast(t('pt.reversed'));
        void qc.invalidateQueries();
        onClose();
      },
      onError: (e) => toast(errText(t, e), 'error'),
    }),
  );
  return (
    <Dialog
      open
      onClose={onClose}
      title={t(target.type === 'ACCRUAL' ? 'pt.reverseAccrual' : 'pt.reverseRedemption')}
      footer={
        <>
          <button type="button" className="bo-btn bo-btn--ghost" onClick={onClose}>
            {t('ui.close')}
          </button>
          <button type="button" className="bo-btn bo-btn--danger" disabled={!reason.trim() || save.isPending || !p.data?.reversible} onClick={() => save.mutate({ ...target, reason })}>
            {t('pt.reverse')}
          </button>
        </>
      }
    >
      {p.data && (
        <p style={{ margin: '0 0 12px' }}>
          {target.type === 'REDEMPTION' ? t('pt.reverseRedemptionText', { n: fmt(p.data.amount) }) : t('pt.reverseAccrualText', { n: fmt(p.data.amount) })}
        </p>
      )}
      {p.data && p.data.spent > 0 && <div className="bo-alert bo-alert--warn" style={{ marginBottom: 12 }}>{t('pt.debtWarn', { spent: fmt(p.data.spent), after: fmt(p.data.balanceAfter) })}</div>}
      <Field label={t('pt.f.reasonRequired')}>
        <input className="bo-input" value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} placeholder={t('pt.reasonPh')} />
      </Field>
      <p className="bo-hint">{t('pt.reverseHint')}</p>
    </Dialog>
  );
}

function RedeemDialog({ clientId, balance, onClose }: { clientId: string; balance: number; onClose: () => void }) {
  const { t } = useT();
  const trpc = useTRPC();
  const qc = useQueryClient();
  const toast = useToast();
  const dict = useDict();
  const [f, setF] = useState({ orderAmount: '', amount: '', orderRef: '', reason: '' });
  const [idem] = useState(key);
  const share = dict.data?.rules.sharePct ?? 30;
  const min = dict.data?.rules.min ?? 1000;
  const max = f.orderAmount ? Math.min(balance, Math.floor((Number(f.orderAmount) * share) / 100)) : balance;
  const save = useMutation(
    trpc.bo.points.redeem.mutationOptions({
      onSuccess: (r) => {
        toast(r.status === 'needs_approval' ? t('pt.needsApproval') : t('pt.redeemed'));
        void qc.invalidateQueries();
        onClose();
      },
      onError: (e) => toast(errText(t, e), 'error'),
    }),
  );
  const amount = Number(f.amount) || 0;
  const num = (v: string) => v.replace(/\D/g, '').slice(0, 9);
  return (
    <Dialog
      open
      onClose={onClose}
      title={t('pt.redeem')}
      footer={
        <>
          <button type="button" className="bo-btn bo-btn--ghost" onClick={onClose}>
            {t('ui.close')}
          </button>
          <button type="button" className="bo-btn" disabled={amount < min || amount > max || !f.reason.trim() || save.isPending} onClick={() => save.mutate({ clientId, amount, orderAmount: Number(f.orderAmount) || null, orderRef: f.orderRef.trim() || null, reason: f.reason, idempotencyKey: idem })}>
            {t('pt.redeemBtn')}
          </button>
        </>
      }
    >
      <div className="bo-row2">
        <Field label={t('pt.f.orderAmount')}>
          <input className="bo-input" inputMode="numeric" value={f.orderAmount} onChange={(e) => setF({ ...f, orderAmount: num(e.target.value) })} />
        </Field>
        <Field label={t('pt.f.orderRef')}>
          <input className="bo-input" value={f.orderRef} maxLength={60} onChange={(e) => setF({ ...f, orderRef: e.target.value })} />
        </Field>
      </div>
      <Field label={t('pt.f.redeemAmount')} hint={t('pt.maxHint', { max: fmt(max), share, min: fmt(min) })}>
        <input className="bo-input" inputMode="numeric" placeholder={String(max)} value={f.amount} onChange={(e) => setF({ ...f, amount: num(e.target.value) })} />
      </Field>
      <Field label={t('pt.f.reasonRequired')}>
        <input className="bo-input" value={f.reason} maxLength={500} placeholder={t('pt.redeemReasonPh')} onChange={(e) => setF({ ...f, reason: e.target.value })} />
      </Field>
      {f.orderAmount && amount > 0 && <p className="bo-hint">{t('pt.toPay', { sum: fmt(Math.max(0, Number(f.orderAmount) - amount)) })}</p>}
    </Dialog>
  );
}

// ───── запросы клиентов ─────

function Requests() {
  const { t, dateTime } = useT();
  const trpc = useTRPC();
  const qc = useQueryClient();
  const toast = useToast();
  const list = useQuery(trpc.bo.points.requests.queryOptions());
  const [sums, setSums] = useState<Record<string, string>>({});
  const [rejecting, setRejecting] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const dict = useDict();
  const share = dict.data?.rules.sharePct ?? 30;
  const done = () => void qc.invalidateQueries();
  const confirm = useMutation(trpc.bo.points.confirm.mutationOptions({ onSuccess: (r) => (toast(t('pt.confirmed', { n: fmt(r.amount) })), done()), onError: (e) => toast(errText(t, e), 'error') }));
  const reject = useMutation(trpc.bo.points.reject.mutationOptions({ onSuccess: () => (setRejecting(null), setReason(''), done()), onError: (e) => toast(errText(t, e), 'error') }));
  if (!list.data) return <Skeleton h={200} />;
  if (!list.data.length) return <div className="bo-panel bo-empty">{t('pt.noRequests')}</div>;
  return (
    <div className="bo-panel" style={{ padding: 8 }}>
      <ul className="bo-list">
        {list.data.map((r) => {
          const orderSum = sums[r.id] ?? (r.order ? String(r.order.totalPrice - r.order.pointsUsed) : '');
          const max = orderSum ? Math.min(r.amount, r.balance, Math.floor((Number(orderSum) * share) / 100)) : 0;
          return (
            <li key={r.id} style={{ flexWrap: 'wrap', gap: 10 }}>
              <span className="bo-grow" style={{ minWidth: 220 }}>
                <a href={`#/points?tab=accrue&client=${r.clientId}`}>
                  <b>{r.name}</b>
                </a>{' '}
                · {t('pt.wants', { n: fmt(r.amount) })}
                <span className="bo-small bo-faint" style={{ display: 'block' }}>
                  {r.order ? t('pt.forOrder', { n: r.order.number }) : r.note ? `💬 ${r.note}` : t('pt.noOrder')} · {t('pt.balanceN', { n: fmt(r.balance) })} · {dateTime(r.requestedAt)}
                </span>
              </span>
              <input className="bo-input" style={{ width: 150 }} inputMode="numeric" placeholder={t('pt.f.orderAmount')} value={orderSum} onChange={(e) => setSums({ ...sums, [r.id]: e.target.value.replace(/\D/g, '') })} />
              <button type="button" className="bo-btn bo-btn--sm" disabled={!max || confirm.isPending} onClick={() => confirm.mutate({ id: r.id, orderAmount: Number(orderSum) })}>
                {max ? t('pt.confirmN', { n: fmt(max) }) : t('pt.confirm')}
              </button>
              <button type="button" className="bo-btn bo-btn--sm bo-btn--ghost" onClick={() => setRejecting(r.id)}>
                {t('pt.reject')}
              </button>
            </li>
          );
        })}
      </ul>
      {rejecting && (
        <Dialog
          open
          onClose={() => setRejecting(null)}
          title={t('pt.reject')}
          footer={
            <button type="button" className="bo-btn" disabled={!reason.trim() || reject.isPending} onClick={() => reject.mutate({ id: rejecting, reason })}>
              {t('pt.reject')}
            </button>
          }
        >
          <Field label={t('pt.rejectReason')}>
            <input className="bo-input" value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} />
          </Field>
        </Dialog>
      )}
    </div>
  );
}

// ───── предложения системы ─────

function Suggestions() {
  const { t, date } = useT();
  const trpc = useTRPC();
  const qc = useQueryClient();
  const toast = useToast();
  const list = useQuery(trpc.bo.points.suggestions.queryOptions());
  const [edit, setEdit] = useState<{ id: string; amount: string; reason: string; mode: 'amount' | 'reject' } | null>(null);
  const done = () => (setEdit(null), void qc.invalidateQueries());
  const accept = useMutation(trpc.bo.points.acceptSuggestion.mutationOptions({ onSuccess: () => (toast(t('pt.accrued', { n: '' })), done()), onError: (e) => toast(errText(t, e), 'error') }));
  const reject = useMutation(trpc.bo.points.rejectSuggestion.mutationOptions({ onSuccess: done, onError: (e) => toast(errText(t, e), 'error') }));
  if (!list.data) return <Skeleton h={200} />;
  if (!list.data.length) return <div className="bo-panel bo-empty">{t('pt.noSuggestions')}</div>;
  const why = (s: (typeof list.data)[number]) => {
    const p = s.payload as Record<string, unknown>;
    if (s.mechanic === 'over_limit') return t('pt.why.over_limit', { by: s.by ?? '—', op: t(`pt.op.${String(p.op)}`) });
    if (s.mechanic === 'photo_review') return t('pt.why.photo_review', { n: String(p.orderNumber ?? '') });
    return t(`pt.why.${s.mechanic}`);
  };
  return (
    <div className="bo-panel" style={{ padding: 8 }}>
      <p className="bo-hint" style={{ padding: '6px 12px 0' }}>
        {t('pt.suggestionsHint')}
      </p>
      <ul className="bo-list">
        {list.data.map((s) => (
          <li key={s.id} style={{ flexWrap: 'wrap', gap: 10 }}>
            <span className="bo-grow" style={{ minWidth: 220 }}>
              <a href={`#/points?tab=accrue&client=${s.clientId}`}>
                <b>{s.name}</b>
              </a>{' '}
              · {t(`pt.mech.${s.mechanic}`)}
              <span className="bo-small bo-faint" style={{ display: 'block' }}>
                {why(s)} · {date(s.createdAt)}
              </span>
            </span>
            <b className="bo-num">{fmt(s.amount)}</b>
            <button type="button" className="bo-btn bo-btn--sm" disabled={accept.isPending} onClick={() => accept.mutate({ id: s.id })}>
              {t(s.mechanic === 'over_limit' ? 'pt.approve' : 'pt.accrueBtn')}
            </button>
            {s.mechanic !== 'over_limit' && (
              <button type="button" className="bo-btn bo-btn--sm bo-btn--ghost" onClick={() => setEdit({ id: s.id, amount: String(s.amount), reason: '', mode: 'amount' })}>
                {t('pt.changeAmount')}
              </button>
            )}
            <button type="button" className="bo-btn bo-btn--sm bo-btn--ghost" onClick={() => setEdit({ id: s.id, amount: '', reason: '', mode: 'reject' })}>
              {t('pt.reject')}
            </button>
          </li>
        ))}
      </ul>
      {edit && (
        <Dialog
          open
          onClose={() => setEdit(null)}
          title={edit.mode === 'reject' ? t('pt.reject') : t('pt.changeAmount')}
          footer={
            <button
              type="button"
              className="bo-btn"
              disabled={!edit.reason.trim() || (edit.mode === 'amount' && !Number(edit.amount))}
              onClick={() => (edit.mode === 'reject' ? reject.mutate({ id: edit.id, reason: edit.reason }) : accept.mutate({ id: edit.id, amount: Number(edit.amount), reason: edit.reason }))}
            >
              {edit.mode === 'reject' ? t('pt.reject') : t('pt.accrueBtn')}
            </button>
          }
        >
          {edit.mode === 'amount' && (
            <Field label={t('pt.f.amount')}>
              <input className="bo-input" inputMode="numeric" value={edit.amount} onChange={(e) => setEdit({ ...edit, amount: e.target.value.replace(/\D/g, '') })} />
            </Field>
          )}
          <Field label={t('pt.f.reasonRequired')}>
            <input className="bo-input" value={edit.reason} maxLength={500} onChange={(e) => setEdit({ ...edit, reason: e.target.value })} />
          </Field>
        </Dialog>
      )}
    </div>
  );
}

// ───── журнал ─────

function Ledger({ dict, initialClient }: { dict: Dict; initialClient: string | null }) {
  const { t, dateTime, locale } = useT();
  const trpc = useTRPC();
  const [f, setF] = useState({ type: '', categoryId: '', channel: '', from: '', to: '' });
  const [rev, setRev] = useState<{ type: 'ACCRUAL' | 'REDEMPTION'; id: string } | null>(null);
  const input = useMemo(
    () => ({
      clientId: initialClient ?? undefined,
      type: (f.type || undefined) as 'accrual' | 'bonus' | 'redemption' | 'reversal' | undefined,
      categoryId: f.categoryId || undefined,
      channel: (f.channel || undefined) as 'WEB' | 'BOT' | 'CSV' | 'AUTO' | 'CLIENT' | undefined,
      from: f.from ? new Date(`${f.from}T00:00:00+04:00`) : undefined,
      to: f.to ? new Date(`${f.to}T24:00:00+04:00`) : undefined,
      take: 500,
    }),
    [f, initialClient],
  );
  const list = useQuery(trpc.bo.points.ledger.queryOptions(input));
  const csv = () => {
    const head = ['date', 'type', 'client', 'amount', 'suggested', 'status', 'what', 'order', 'orderAmount', 'tier', 'rate', 'author', 'channel', 'reason', 'comment'];
    const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const rows = (list.data ?? []).map((r) => [r.at.toISOString(), r.type, r.client, r.amount, r.suggested, r.status, pick(r.what, locale), r.orderRef, r.orderAmount, r.tier, r.ratePct, r.author ?? 'system', r.channel, r.reason, r.comment].map(esc).join(','));
    download(`bariki-${todayKey()}.csv`, '﻿' + [head.join(','), ...rows].join('\n'));
  };
  return (
    <div className="bo-panel" style={{ padding: 8 }}>
      <div className="bo-pt-filters">
        <select className="bo-select" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })}>
          <option value="">{t('pt.allTypes')}</option>
          {['accrual', 'bonus', 'redemption', 'reversal'].map((x) => (
            <option key={x} value={x}>
              {t(`pt.type.${x}`)}
            </option>
          ))}
        </select>
        <select className="bo-select" value={f.categoryId} onChange={(e) => setF({ ...f, categoryId: e.target.value })}>
          <option value="">{t('pt.allCategories')}</option>
          {dict.categories.map((c) => (
            <option key={c.id} value={c.id}>
              {pick(c.name, locale)}
            </option>
          ))}
        </select>
        <select className="bo-select" value={f.channel} onChange={(e) => setF({ ...f, channel: e.target.value })}>
          <option value="">{t('pt.allChannels')}</option>
          {['WEB', 'BOT', 'CSV', 'AUTO', 'CLIENT'].map((x) => (
            <option key={x} value={x}>
              {t(`pt.channel.${x}`)}
            </option>
          ))}
        </select>
        <input className="bo-input" type="date" value={f.from} onChange={(e) => setF({ ...f, from: e.target.value })} />
        <input className="bo-input" type="date" value={f.to} onChange={(e) => setF({ ...f, to: e.target.value })} />
        <button type="button" className="bo-btn bo-btn--sm" onClick={csv} disabled={!list.data?.length}>
          <I name="download" size={16} /> CSV
        </button>
        {initialClient && (
          <a className="bo-btn bo-btn--sm bo-btn--ghost" href="#/points?tab=ledger">
            {t('pt.allClients')}
          </a>
        )}
      </div>
      {!list.data ? (
        <Skeleton h={200} />
      ) : list.data.length === 0 ? (
        <p className="bo-empty">{t('pt.noOps')}</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="bo-table">
            <thead>
              <tr>
                <th>{t('pt.col.date')}</th>
                <th>{t('pt.col.client')}</th>
                <th>{t('pt.col.what')}</th>
                <th className="bo-r">{t('pt.col.amount')}</th>
                <th>{t('pt.col.status')}</th>
                <th>{t('pt.col.author')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {list.data.map((r) => (
                <tr key={`${r.type}:${r.id}`} data-hl={r.highlight || undefined}>
                  <td className="bo-small">{dateTime(r.at)}</td>
                  <td>
                    <a href={`#/points?tab=accrue&client=${r.clientId}`}>{r.client}</a>
                  </td>
                  <td className="bo-small">
                    <b>{r.type === 'accrual' || r.type === 'bonus' ? pick(r.what, locale) : t(`pt.type.${r.type}`)}</b>
                    {r.orderRef ? ` ${r.orderRef}` : ''}
                    {r.orderAmount ? ` · ${fmt(r.orderAmount)} ֏` : ''}
                    {r.tier ? ` · ${TIER[r.tier]} ${r.ratePct}%` : ''}
                    {(r.reason || r.comment) && (
                      <span className="bo-faint" style={{ display: 'block' }}>
                        {[r.reason, r.comment?.startsWith('debt:') ? t('pt.debtN', { n: fmt(Number(r.comment.slice(5))) }) : r.comment].filter(Boolean).join(' · ')}
                      </span>
                    )}
                  </td>
                  <td className="bo-r bo-num">
                    <b style={{ color: r.amount < 0 ? 'var(--bc-danger)' : undefined }}>
                      {r.amount > 0 ? '+' : '−'}
                      {fmt(Math.abs(r.amount))}
                    </b>
                    {r.suggested != null && r.suggested !== r.amount && <span className="bo-small bo-faint" style={{ display: 'block' }}>{t('pt.suggestedN', { n: fmt(r.suggested) })}</span>}
                  </td>
                  <td className="bo-small">{t(`pt.status.${r.status}`)}</td>
                  <td className="bo-small">
                    {r.author ?? t('pt.system')}
                    <span className="bo-faint" style={{ display: 'block' }}>
                      {t(`pt.channel.${r.channel}`)}
                    </span>
                  </td>
                  <td>
                    {r.canReverse && (
                      <button type="button" className="bo-btn bo-btn--sm bo-btn--ghost" onClick={() => setRev({ type: r.type === 'redemption' ? 'REDEMPTION' : 'ACCRUAL', id: r.id })}>
                        {t('pt.reverse')}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {rev && <ReverseDialog target={rev} onClose={() => setRev(null)} />}
    </div>
  );
}

// ───── отчёт ─────

function Report() {
  const { t, locale } = useT();
  const trpc = useTRPC();
  const monthAgo = new Date(Date.now() - 30 * 86_400_000 + 4 * 3600_000).toISOString().slice(0, 10);
  const [p, setP] = useState({ from: monthAgo, to: todayKey() });
  const r = useQuery(trpc.bo.points.report.queryOptions({ from: new Date(`${p.from}T00:00:00+04:00`), to: new Date(`${p.to}T24:00:00+04:00`) }));
  return (
    <>
      <div className="bo-pt-filters" style={{ marginBottom: 14 }}>
        <input className="bo-input" type="date" value={p.from} onChange={(e) => setP({ ...p, from: e.target.value })} />
        <input className="bo-input" type="date" value={p.to} onChange={(e) => setP({ ...p, to: e.target.value })} />
      </div>
      {!r.data ? (
        <Skeleton h={260} />
      ) : (
        <div className="bo-client-grid">
          <div className="bo-col">
            <section className="bo-panel">
              <div className="bo-stats">
                {(['accrued', 'spent', 'expired', 'reversed', 'liabilities'] as const).map((k) => (
                  <span key={k}>
                    <span className="bo-small bo-faint">{t(`pt.rep.${k}`)}</span>
                    <b>{fmt(r.data[k])}</b>
                  </span>
                ))}
                <span>
                  <span className="bo-small bo-faint">{t('pt.rep.rate')}</span>
                  <b>{r.data.redemptionRate}%</b>
                </span>
              </div>
            </section>
            <section className="bo-panel">
              <div className="bo-panel__head">
                <h2 className="bo-h2">{t('pt.rep.byCategory')}</h2>
              </div>
              <ul className="bo-list">
                {r.data.byCategory.map((c) => (
                  <li key={c.code}>
                    <span className="bo-grow">{pick(c.name, locale) || c.code}</span>
                    <span className="bo-small bo-faint">{t('pt.rep.cost', { pct: c.costPct, revenue: fmt(c.revenue) })}</span>
                    <b className="bo-num">{fmt(c.points)}</b>
                  </li>
                ))}
              </ul>
            </section>
            <section className="bo-panel">
              <div className="bo-panel__head">
                <h2 className="bo-h2">{t('pt.rep.retention')}</h2>
              </div>
              <ul className="bo-list">
                {r.data.retention.map((x) => (
                  <li key={String(x.withPoints)}>
                    <span className="bo-grow">{t(x.withPoints ? 'pt.rep.with' : 'pt.rep.without')}</span>
                    <span className="bo-small bo-faint">{t('pt.rep.clientsN', { n: x.clients, multi: x.multi })}</span>
                    <b className="bo-num">{x.repeatPct}%</b>
                  </li>
                ))}
              </ul>
            </section>
          </div>
          <div className="bo-col">
            <section className="bo-panel">
              <div className="bo-panel__head">
                <h2 className="bo-h2">{t('pt.rep.topClients')}</h2>
              </div>
              <ul className="bo-list">
                {r.data.topClients.map((c) => (
                  <li key={c.id}>
                    <a className="bo-grow" href={`#/points?tab=accrue&client=${c.id}`}>
                      {c.name}
                    </a>
                    <b className="bo-num">{fmt(c.balance)}</b>
                  </li>
                ))}
              </ul>
            </section>
            <section className="bo-panel">
              <div className="bo-panel__head">
                <h2 className="bo-h2">{t('pt.rep.topAgencies')}</h2>
              </div>
              {r.data.topAgencies.length === 0 ? (
                <p className="bo-hint">—</p>
              ) : (
                <ul className="bo-list">
                  {r.data.topAgencies.map((a) => (
                    <li key={a.name}>
                      <span className="bo-grow">{a.name}</span>
                      <b className="bo-num">{a.n}</b>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        </div>
      )}
    </>
  );
}

// ───── импорт CSV ─────

function parseCsv(text: string) {
  const lines = text.replace(/\r/g, '').split('\n').filter((l) => l.trim());
  const sep = lines[0]?.includes(';') ? ';' : ',';
  const rows = lines.map((l) => l.split(sep).map((c) => c.trim().replace(/^"|"$/g, '')));
  // первая строка — заголовок, если в третьей колонке не число
  const body = rows[0] && Number.isNaN(Number(rows[0][2]?.replace(/\s/g, ''))) ? rows.slice(1) : rows;
  return body.map((r) => ({ phone: r[0] ?? '', category: r[1] ?? '', orderAmount: Number((r[2] ?? '').replace(/[^\d]/g, '')) || 0, date: r[3] ?? '', orderRef: r[4] ?? '' }));
}

function Import() {
  const { t } = useT();
  const trpc = useTRPC();
  const qc = useQueryClient();
  const toast = useToast();
  const [rows, setRows] = useState<ReturnType<typeof parseCsv>>([]);
  const run = useMutation(trpc.bo.points.import.mutationOptions({ onError: (e) => toast(errText(t, e), 'error') }));
  const preview = run.data && run.variables?.dryRun ? run.data : null;
  const commit = useMutation(
    trpc.bo.points.import.mutationOptions({
      onSuccess: (r) => {
        toast(t('pt.importDone', { n: r.rows.filter((x) => !x.error).length }));
        setRows([]);
        run.reset();
        void qc.invalidateQueries();
      },
      onError: (e) => toast(errText(t, e), 'error'),
    }),
  );
  return (
    <section className="bo-panel">
      <p className="bo-hint">{t('pt.importHint')}</p>
      <input
        type="file"
        accept=".csv,text/csv"
        onChange={async (e) => {
          const file = e.target.files?.[0];
          if (!file) return;
          const parsed = parseCsv(await file.text());
          setRows(parsed);
          run.mutate({ dryRun: true, rows: parsed });
        }}
      />
      {preview && (
        <>
          <div className="bo-stats" style={{ margin: '14px 0' }}>
            <span>
              <span className="bo-small bo-faint">{t('pt.importRows')}</span>
              <b>{preview.rows.length}</b>
            </span>
            <span>
              <span className="bo-small bo-faint">{t('pt.importTotal')}</span>
              <b>{fmt(preview.total)}</b>
            </span>
          </div>
          <ul className="bo-list">
            {preview.rows.map((r) => (
              <li key={r.line}>
                <span className="bo-faint bo-num">{r.line}</span>
                <span className="bo-grow">{r.client ?? rows[r.line - 1]?.phone}</span>
                {r.error ? <span className="bo-chip bo-chip--danger">{t(`pt.importErr.${r.error}`)}</span> : <b className="bo-num">+{fmt(r.amount ?? 0)}</b>}
              </li>
            ))}
          </ul>
          <button type="button" className="bo-btn bo-btn--accent" style={{ marginTop: 12 }} disabled={commit.isPending || preview.rows.every((r) => r.error)} onClick={() => commit.mutate({ dryRun: false, rows })}>
            {t('pt.importCommit', { n: preview.rows.filter((r) => !r.error).length })}
          </button>
        </>
      )}
    </section>
  );
}

// ───── справочники ─────

function Setup({ dict }: { dict: Dict }) {
  const { t, locale } = useT();
  const [cat, setCat] = useState<Dict['categories'][number] | 'new' | null>(null);
  const [bt, setBt] = useState<Dict['bonusTypes'][number] | 'new' | null>(null);
  return (
    <div className="bo-client-grid">
      <section className="bo-panel">
        <div className="bo-panel__head">
          <h2 className="bo-h2">{t('pt.categories')}</h2>
          <button type="button" className="bo-btn bo-btn--sm bo-btn--ghost" onClick={() => setCat('new')}>
            <I name="plus" size={14} />
          </button>
        </div>
        <table className="bo-table">
          <thead>
            <tr>
              <th />
              <th className="bo-r">Barev</th>
              <th className="bo-r">Barev+</th>
              <th className="bo-r">Elite</th>
            </tr>
          </thead>
          <tbody>
            {dict.categories.map((c) => (
              <tr key={c.id} onClick={() => setCat(c)} style={{ cursor: 'pointer' }} data-off={!c.active || undefined}>
                <td>{pick(c.name, locale)}</td>
                <td className="bo-r bo-num">{c.rateBarev}%</td>
                <td className="bo-r bo-num">{c.ratePlus}%</td>
                <td className="bo-r bo-num">{c.rateElite}%</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="bo-hint" style={{ marginTop: 10 }}>
          {t('pt.rulesHint', { hours: dict.rules.pendingHours, share: dict.rules.sharePct, min: fmt(dict.rules.min), burn: dict.rules.burnDays })}
        </p>
      </section>
      <section className="bo-panel">
        <div className="bo-panel__head">
          <h2 className="bo-h2">{t('pt.bonusTypes')}</h2>
          <button type="button" className="bo-btn bo-btn--sm bo-btn--ghost" onClick={() => setBt('new')}>
            <I name="plus" size={14} />
          </button>
        </div>
        <ul className="bo-list">
          {dict.bonusTypes.map((b) => (
            <li key={b.id} onClick={() => setBt(b)} style={{ cursor: 'pointer', opacity: b.active ? 1 : 0.5 }}>
              <span className="bo-grow">
                <b>{pick(b.name, locale)}</b>
                <span className="bo-small bo-faint" style={{ display: 'block' }}>
                  {b.ttlDays ? t('pt.forDays', { n: b.ttlDays }) : t('pt.noTtl')}
                </span>
              </span>
              <b className="bo-num">{fmt(b.defaultAmount)}</b>
            </li>
          ))}
        </ul>
      </section>
      {cat && <CategoryDialog row={cat === 'new' ? null : cat} onClose={() => setCat(null)} />}
      {bt && <BonusDialog row={bt === 'new' ? null : bt} onClose={() => setBt(null)} />}
    </div>
  );
}

const LANGS = ['ru', 'en', 'hy', 'zh', 'es'] as const;
type L5 = Record<(typeof LANGS)[number], string>;
const l5 = (v?: Record<string, string>): L5 => ({ ru: v?.ru ?? '', en: v?.en ?? '', hy: v?.hy ?? '', zh: v?.zh ?? '', es: v?.es ?? '' });

function NameFields({ value, onChange }: { value: L5; onChange: (v: L5) => void }) {
  return (
    <div className="bo-row2">
      {LANGS.map((l) => (
        <Field key={l} label={l.toUpperCase()}>
          <input className="bo-input" value={value[l]} maxLength={100} onChange={(e) => onChange({ ...value, [l]: e.target.value })} />
        </Field>
      ))}
    </div>
  );
}

function CategoryDialog({ row, onClose }: { row: Dict['categories'][number] | null; onClose: () => void }) {
  const { t } = useT();
  const trpc = useTRPC();
  const qc = useQueryClient();
  const toast = useToast();
  const [f, setF] = useState({ code: row?.code ?? '', name: l5(row?.name), rateBarev: String(row?.rateBarev ?? 3), ratePlus: String(row?.ratePlus ?? 5), rateElite: String(row?.rateElite ?? 7), active: row?.active ?? true });
  const save = useMutation(trpc.bo.points.saveCategory.mutationOptions({ onSuccess: () => (void qc.invalidateQueries(), onClose()), onError: (e) => toast(errText(t, e), 'error') }));
  return (
    <Dialog
      open
      onClose={onClose}
      title={t('pt.categories')}
      footer={
        <button type="button" className="bo-btn" disabled={!f.code || !f.name.ru || save.isPending} onClick={() => save.mutate({ id: row?.id, code: f.code, name: f.name, rateBarev: Number(f.rateBarev) || 0, ratePlus: Number(f.ratePlus) || 0, rateElite: Number(f.rateElite) || 0, active: f.active, sortOrder: row?.sortOrder ?? 99 })}>
          {t('ui.save')}
        </button>
      }
    >
      <Field label={t('pt.code')}>
        <input className="bo-input" value={f.code} disabled={!!row} onChange={(e) => setF({ ...f, code: e.target.value.toLowerCase().replace(/[^a-z_]/g, '') })} />
      </Field>
      <NameFields value={f.name} onChange={(name) => setF({ ...f, name })} />
      <div className="bo-row2">
        {(['rateBarev', 'ratePlus', 'rateElite'] as const).map((k) => (
          <Field key={k} label={`${k === 'rateBarev' ? 'Barev' : k === 'ratePlus' ? 'Barev+' : 'Elite'}, %`}>
            <input className="bo-input" inputMode="numeric" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value.replace(/\D/g, '').slice(0, 3) })} />
          </Field>
        ))}
      </div>
      <label className="bo-check">
        <input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} />
        {t('pt.activeLabel')}
      </label>
    </Dialog>
  );
}

function BonusDialog({ row, onClose }: { row: Dict['bonusTypes'][number] | null; onClose: () => void }) {
  const { t } = useT();
  const trpc = useTRPC();
  const qc = useQueryClient();
  const toast = useToast();
  const [f, setF] = useState({ code: row?.code ?? '', name: l5(row?.name), defaultAmount: String(row?.defaultAmount ?? 1000), ttlDays: row?.ttlDays ? String(row.ttlDays) : '', active: row?.active ?? true });
  const save = useMutation(trpc.bo.points.saveBonusType.mutationOptions({ onSuccess: () => (void qc.invalidateQueries(), onClose()), onError: (e) => toast(errText(t, e), 'error') }));
  return (
    <Dialog
      open
      onClose={onClose}
      title={t('pt.bonusTypes')}
      footer={
        <button type="button" className="bo-btn" disabled={!f.code || !f.name.ru || save.isPending} onClick={() => save.mutate({ id: row?.id, code: f.code, name: f.name, defaultAmount: Number(f.defaultAmount) || 0, ttlDays: Number(f.ttlDays) || null, active: f.active, sortOrder: row?.sortOrder ?? 99 })}>
          {t('ui.save')}
        </button>
      }
    >
      <Field label={t('pt.code')}>
        <input className="bo-input" value={f.code} disabled={!!row} onChange={(e) => setF({ ...f, code: e.target.value.toLowerCase().replace(/[^a-z_]/g, '') })} />
      </Field>
      <NameFields value={f.name} onChange={(name) => setF({ ...f, name })} />
      <div className="bo-row2">
        <Field label={t('pt.f.defaultAmount')}>
          <input className="bo-input" inputMode="numeric" value={f.defaultAmount} onChange={(e) => setF({ ...f, defaultAmount: e.target.value.replace(/\D/g, '') })} />
        </Field>
        <Field label={t('pt.f.ttl')} hint={t('pt.f.ttlNone')}>
          <input className="bo-input" inputMode="numeric" value={f.ttlDays} onChange={(e) => setF({ ...f, ttlDays: e.target.value.replace(/\D/g, '').slice(0, 4) })} />
        </Field>
      </div>
      <label className="bo-check">
        <input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} />
        {t('pt.activeLabel')}
      </label>
    </Dialog>
  );
}
